import { isRecord } from "@/core/utils";
import type {
  FichaFinanceiraCriada,
  FichaFinanceiraDetalhe,
  FichaPrevia,
} from "@/modules/financeiro-fichas";
import {
  FICHAS_NO_VALUE,
  formatDataBr,
  formatElaboradoEm,
  formatNumero,
} from "@/screens/financeiro/obras/fichas/ficha-formatacao/ficha-formatacao";
import { montarPayloadFicha } from "../../hooks/ficha-previa-form";
import type { FichaCampoEditavel, FichaFormValues } from "../../hooks/ficha-previa-form";
import type { ResponsaveisOpcoesState } from "../../hooks/use-responsaveis-opcoes";
import type { FichaField, FichaSection } from "../ficha-secao";
import {
  CAMPOS_USUARIO,
  CONDICAO_DRY_DOCKING,
  DB_ONLY_SECTION_IDS,
  FICHA_SECTIONS,
  NAO_APLICAVEL,
  NAO_APLICAVEL_MENSAGEM,
} from "./financeiro-ficha-viewer.constants";

/** Estado dos campos editáveis na prévia; ausente = ficha somente leitura. */
export interface FichaEdicao {
  values: FichaFormValues;
  errors: Partial<Record<FichaCampoEditavel, string>>;
  alterar: (campo: FichaCampoEditavel, valor: string) => void;
  responsaveis: ResponsaveisOpcoesState;
}

/** Campo da ficha; com `edicao`, vira editável (texto ou seletor de profissional). */
function campoDaFicha(
  label: string,
  nome: FichaCampoEditavel,
  salvo: string | null,
  edicao?: FichaEdicao,
): FichaField {
  if (!edicao) return { label, value: salvo ?? FICHAS_NO_VALUE };

  const usuario = CAMPOS_USUARIO.has(nome);
  return {
    label,
    value: edicao.values[nome],
    edit: {
      name: nome,
      testId: `ficha-campo-${nome}`,
      inputTestId: `ficha-campo-${nome}-input`,
      kind: usuario ? "usuario" : "texto",
      value: edicao.values[nome],
      error: edicao.errors[nome],
      onChange: (valor) => edicao.alterar(nome, valor),
      ...(usuario
        ? {
            opcoes: edicao.responsaveis.opcoes,
            opcoesErro: edicao.responsaveis.errorMessage,
            carregando: edicao.responsaveis.loading,
          }
        : {}),
    },
  };
}

/** Ficha a exibir na geração: dados da prévia mais os campos editáveis (e, salva, quem a gerou). */
export function fichaDaPrevia(
  obraId: string,
  previa: FichaPrevia,
  values: FichaFormValues,
  salva: FichaFinanceiraCriada | null,
  solicitanteLogado = "",
): FichaFinanceiraDetalhe {
  return {
    id: salva?.id ?? "",
    obraId,
    criadoEm: salva?.elaboradoEm || previa.elaboradoEm,
    solicitante: salva?.geradoPor ?? solicitanteLogado,
    statusFel: null,
    obra: previa.obra,
    informacoesEmbarcacao: previa.informacoesEmbarcacao,
    custosFinanceiro: previa.custosFinanceiro,
    ...montarPayloadFicha(values),
    dadosFicha: null,
  };
}

/** `86 dias` + composição `(70 de Obra + 10 de Teste + 6 de Aceitação)` das etapas conhecidas. */
function duracaoPrevista(obra: FichaFinanceiraDetalhe["obra"]): FichaField {
  const { obraDias, testesDias, aceitacaoDias } = obra.etapas;
  const partes = [
    obraDias === null ? null : `${obraDias} de Obra`,
    testesDias === null ? null : `${testesDias} de Teste`,
    aceitacaoDias === null ? null : `${aceitacaoDias} de Aceitação`,
  ].filter((parte): parte is string => parte !== null);

  return {
    label: "Duração prevista da obra",
    value: `${obra.duracaoDias} ${obra.duracaoDias === 1 ? "dia" : "dias"}`,
    hint: partes.length > 0 ? `(${partes.join(" + ")})` : undefined,
  };
}

/** `807 m² (58,5 m x 13,8 m)`; sem área, "—"; sem medidas, só a área. */
function dimensoesDoDeck(info: FichaFinanceiraDetalhe["informacoesEmbarcacao"]): string {
  if (info.convesAreaTotal === null) return FICHAS_NO_VALUE;

  const area = formatNumero(info.convesAreaTotal, " m²");
  if (info.convesComprimento === null || info.convesLargura === null) return area;

  return `${area} (${formatNumero(info.convesComprimento, " m")} x ${formatNumero(info.convesLargura, " m")})`;
}

/** Linhas de "Informações da Embarcação" (RN010: fora de Dry Docking, calado e deslocamento são N/A). */
function informacoesDaEmbarcacao(ficha: FichaFinanceiraDetalhe): FichaField[] {
  const info = ficha.informacoesEmbarcacao;
  const seco = ficha.obra.condicao === CONDICAO_DRY_DOCKING;

  return [
    { label: "Bandeira", value: info.bandeira ?? FICHAS_NO_VALUE },
    { label: "Número IMO", value: info.imo ?? FICHAS_NO_VALUE },
    // Sem coluna no modelo atual: aparecem como "—".
    { label: "Porto de Registro", value: FICHAS_NO_VALUE },
    { label: "Deadweight (ton)", value: formatNumero(info.deadweight) },
    { label: "Arqueação Bruta", value: formatNumero(info.arqueacaoBruta) },
    { label: "Arqueação Líquida", value: formatNumero(info.arqueacaoLiquida) },
    { label: "LOA (m)", value: formatNumero(info.loa) },
    { label: "Boca moldada (m)", value: formatNumero(info.bocaMoldada) },
    { label: "Pontal Moldado (m)", value: FICHAS_NO_VALUE },
    { label: "Calado Máximo (m)", value: formatNumero(info.caladoMaximo) },
    {
      label: "Calado previsto na entrada (m)",
      value: seco ? formatNumero(info.caladoPrevistoEntrada) : NAO_APLICAVEL,
      hint: seco ? undefined : NAO_APLICAVEL_MENSAGEM,
    },
    { label: "Dimensões do Deck", value: dimensoesDoDeck(info) },
    { label: "Resistência do Deck", value: formatNumero(info.convesResistencia, " t/m²") },
    { label: "Capacidade do Deck", value: formatNumero(info.convesCapacidade, "t") },
    { label: "Deslocamento máximo (ton)", value: FICHAS_NO_VALUE },
    {
      label: "Deslocamento previsto na entrada",
      value: seco ? formatNumero(info.deslocamentoPrevistoEntrada) : NAO_APLICAVEL,
      hint: seco ? undefined : NAO_APLICAVEL_MENSAGEM,
    },
  ];
}

/** `dataInicio` → `Data Inicio`. */
function humanizar(chave: string): string {
  const separado = chave
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return separado.charAt(0).toUpperCase() + separado.slice(1);
}

function formatarValor(value: unknown): string {
  if (typeof value === "string") return value.trim() || FICHAS_NO_VALUE;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : FICHAS_NO_VALUE;
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (Array.isArray(value)) {
    const partes = value
      .filter((item) => typeof item === "string" || typeof item === "number")
      .map(String);
    return partes.length > 0 ? partes.join(", ") : FICHAS_NO_VALUE;
  }
  return FICHAS_NO_VALUE;
}

/** Pares chave/valor de um objeto simples; objetos aninhados são ignorados. */
function camposDoJson(raw: unknown): FichaField[] {
  if (!isRecord(raw) || Array.isArray(raw)) return [];

  return Object.entries(raw)
    .filter(([, value]) => !isRecord(value) || Array.isArray(value))
    .map(([chave, value]) => ({ label: humanizar(chave), value: formatarValor(value) }));
}

/** Valor de `dadosFicha` para a seção: primeira chave presente, ou `undefined`. */
function dadosDaSecao(dados: Record<string, unknown> | null, chaves: string[]): unknown {
  if (!dados) return undefined;
  const chave = chaves.find(
    (candidata) => dados[candidata] !== undefined && dados[candidata] !== null,
  );
  return chave === undefined ? undefined : dados[chave];
}

/**
 * Monta as 8 seções da ficha a partir da resposta. Identificação, Datas, Contrato, Custos e
 * Embarcação vêm só do banco; Responsáveis soma o que vier em `dadosFicha` (formato definido na
 * HU 2684, lido de forma defensiva) e Anexos vêm do endpoint próprio (HU 2688). Seção sem dado mostra "—".
 */
export function buildFichaSections(
  ficha: FichaFinanceiraDetalhe,
  edicao?: FichaEdicao,
): FichaSection[] {
  const doBanco: Record<string, FichaField[]> = {
    identificacao: [
      { label: "ID da Obra", value: ficha.obra.codigo || FICHAS_NO_VALUE },
      { label: "Nome da embarcação", value: ficha.obra.embarcacao || FICHAS_NO_VALUE },
      { label: "Condição da obra", value: ficha.obra.condicao ?? FICHAS_NO_VALUE },
      { label: "Local estimado da obra", value: ficha.obra.localEstimado ?? FICHAS_NO_VALUE },
    ],
    datas: [
      { label: "Elaborado em", value: formatElaboradoEm(ficha.criadoEm) },
      {
        label: "Data prevista da obra",
        value: `${formatDataBr(ficha.obra.inicio)} a ${formatDataBr(ficha.obra.fim)}`,
      },
      duracaoPrevista(ficha.obra),
    ],
    contrato: [
      { label: "Empresa do Grupo", value: ficha.obra.empresaGrupo ?? FICHAS_NO_VALUE },
      campoDaFicha("Cliente Final", "clienteFinal", ficha.clienteFinal, edicao),
      campoDaFicha("Contrato", "contrato", ficha.contrato, edicao),
    ],
    custos: [
      {
        label: "Descrição da Obra",
        value: ficha.custosFinanceiro.descricaoObra ?? FICHAS_NO_VALUE,
      },
      { label: "Centro de Custo", value: ficha.custosFinanceiro.centroCusto ?? FICHAS_NO_VALUE },
    ],
    embarcacao: informacoesDaEmbarcacao(ficha),
    responsaveis: [
      campoDaFicha("Diretor de Projeto", "diretorProjeto", ficha.diretorProjeto, edicao),
      campoDaFicha(
        "Responsável pela Preparação",
        "responsavelPreparacao",
        ficha.responsavelPreparacao,
        edicao,
      ),
      campoDaFicha(
        "Responsável pela Execução",
        "responsavelExecucao",
        ficha.responsavelExecucao,
        edicao,
      ),
      campoDaFicha("Coordenador", "coordenador", ficha.coordenador, edicao),
      { label: "Solicitante", value: ficha.solicitante || FICHAS_NO_VALUE },
    ],
  };

  return FICHA_SECTIONS.map(({ id, title, kind, dataKeys }): FichaSection => {
    const dados = dadosDaSecao(ficha.dadosFicha, dataKeys);
    const base = {
      id,
      title,
      kind,
      testId: `ficha-section-${id}`,
      menuTestId: `ficha-menu-${id}`,
    };

    if (kind === "orcamento") return { ...base, fields: [] };
    if (kind === "anexos") return { ...base, fields: [] };

    const conhecidos = doBanco[id] ?? [];
    if (kind === "custos") {
      return { ...base, fields: conhecidos, custosTipos: ficha.custosFinanceiro.tiposObra };
    }
    if (DB_ONLY_SECTION_IDS.has(id)) return { ...base, fields: conhecidos };

    const rotulos = new Set(conhecidos.map((campo) => campo.label));
    const extras = camposDoJson(dados).filter((campo) => !rotulos.has(campo.label));

    return { ...base, fields: [...conhecidos, ...extras] };
  });
}
